import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import axios from 'axios'

// ===== Endpoints =====
// Public (no auth)
const API_URL = 'http://108.171.195.159:8000/api/project/project-packages/list/'
// Auth required (use axiosInstance)
const CREATE_PROJECT_URL = 'http://108.171.195.159:8000/api/project/projects/'
const ASSIGN_USER_URL = 'http://108.171.195.159:8000/api/project/assign-user/'

// ===== Axios instance with auth =====
const axiosInstance = axios.create()
axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
})

const FeatureCard = ({ title, description, delay }) => {
    const cardRef = useRef(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-slide-in')
                }
            },
            { threshold: 0.2 }
        )
        if (cardRef.current) observer.observe(cardRef.current)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={cardRef}
            className="p-6 rounded-xl bg-gradient-to-br from-green-800/50 to-teal-800/50 backdrop-blur-md hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-green-500/50 opacity-0"
            style={{ animationDelay: `${delay}s` }}
        >
            <h3 className="text-2xl font-semibold text-green-300">{title}</h3>
            <p className="mt-4 text-gray-300">{description}</p>
        </div>
    )
}

const Home = () => {
    const [authStatus, setAuthStatus] = useState('unauthenticated')
    const [token, setToken] = useState('')
    const [userId, setUserId] = useState(null)
    const [username, setUsername] = useState('')

    const [packages, setPackages] = useState([])
    const [loadingPackages, setLoadingPackages] = useState(true)
    const [pkgError, setPkgError] = useState(null)

    const [buyingId, setBuyingId] = useState(null)
    const [buyMsg, setBuyMsg] = useState(null)
    const [buyErr, setBuyErr] = useState(null)

    const navigate = useNavigate()

    // --- Auth status check ---
    useEffect(() => {
        const t = localStorage.getItem('accessToken')
        if (!t) {
            setAuthStatus('unauthenticated')
            setToken('')
            setUserId(null)
            setUsername('')
            return
        }

        try {
            const decoded = jwtDecode(t)
            setToken(t)
            setUserId(decoded?.user_id || decoded?.id || decoded?.sub || null)
            setUsername(decoded?.username || decoded?.name || 'user')

            setAuthStatus('authenticated')
        } catch {
            setAuthStatus('unauthenticated')
            setToken('')
            setUserId(null)
        }
    }, [])

    const handleLogout = () => {
        localStorage.removeItem('accessToken')
        window.location.reload()
    }

    // --- Fetch the first 3 packages from API (public, no auth) ---
    useEffect(() => {
        const ac = new AbortController()
        ;(async () => {
            try {
                setLoadingPackages(true)
                setPkgError(null)

                const res = await fetch(API_URL, { signal: ac.signal })
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const data = await res.json()
                const firstThree = Array.isArray(data) ? data.slice(0, 3) : []
                setPackages(firstThree)
            } catch (err) {
                if (err.name !== 'AbortError')
                    setPkgError(err.message || 'Failed to load packages')
            } finally {
                setLoadingPackages(false)
            }
        })()
        return () => ac.abort()
    }, [])

    // --- Background particles ---
    useEffect(() => {
        const particlesContainer = document.createElement('div')
        particlesContainer.className = 'particles'
        const root = document.querySelector('.home-container')
        if (root) root.prepend(particlesContainer)
        for (let i = 0; i < 20; i++) {
            const particle = document.createElement('div')
            particle.className = 'particle'
            particle.style.width = `${Math.random() * 5 + 2}px`
            particle.style.height = particle.style.width
            particle.style.left = `${Math.random() * 100}%`
            particle.style.animationDuration = `${Math.random() * 5 + 5}s`
            particle.style.animationDelay = `${Math.random() * 5}s`
            particlesContainer.appendChild(particle)
        }
        return () => particlesContainer.remove()
    }, [])

    const formatPrice = (priceStr) => {
        const n = Number.parseFloat(priceStr)
        if (Number.isNaN(n)) return priceStr
        return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo`
    }

    const toGB = (mb) => {
        if (typeof mb !== 'number') return mb
        return `${Math.round(mb / 1024)} GB`
    }

    const slugify = (s) =>
        s
            .toString()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)+/g, '')

    // --- Buy flow: require login → create project → assign user as owner ---
    const handleBuy = async (pkg) => {
        // Require login
        if (!token) {
            navigate('/login?next=/')
            return
        }

        setBuyingId(pkg.id)
        setBuyErr(null)
        setBuyMsg('Creating project…')

        // Auto-generate a name from username + package
        const nameBase = `${username || 'user'}-${slugify(pkg.name)}`
        const rand = Math.random().toString(36).slice(2, 6)
        const projectName = `${nameBase}-${rand}`

        try {
            // 1) Create project (axiosInstance adds Authorization automatically)
            const createRes = await fetch(CREATE_PROJECT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: projectName,
                    project_type_id: pkg.id,
                    description: `Auto-created from ${pkg.name} on GreenCloud Home`,
                }),
            })
            const created = createRes?.data || {}

            setBuyMsg('Project created. Assigning owner…')

            // 2) Assign user to project (owner/admin)
            if (!userId) throw new Error('Cannot find user_id in token')
            const assignRes = await axiosInstance.post(ASSIGN_USER_URL, {
                user: Number(userId),
                project: created.project_id,
            })
            const assigned = assignRes?.data || {}
            if (assignRes.status < 200 || assignRes.status >= 300) {
                throw new Error(
                    `Assign failed: ${assigned?.error || 'Unknown error'}`
                )
            }

            setBuyMsg('✅ Project ready and ownership assigned. Redirecting…')
            setTimeout(() => navigate('/dashboard'), 1200)
        } catch (e) {
            const msg =
                e?.response?.data?.error ||
                e?.response?.data?.detail ||
                e.message ||
                'Failed to buy package'
            setBuyErr(msg)
            setBuyMsg(null)
        } finally {
            setBuyingId(null)
        }
    }

    return (
        <div className="home-container min-h-screen bg-gradient-to-br from-green-900 via-teal-800 to-blue-900 text-white relative">
            <style>{`
        .particles { position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; z-index: 0; }
        .particle { position: absolute; background: rgba(0, 255, 200, 0.5); border-radius: 50%; animation: float 10s infinite; }
        @keyframes float { 0% { transform: translateY(0); opacity: 0.5; } 50% { opacity: 1; } 100% { transform: translateY(-100vh); opacity: 0.5; } }
        .animate-slide-in { animation: slideIn 0.5s ease-out forwards; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

            <header className="fixed top-0 left-0 w-full z-50 bg-gradient-to-r from-green-900 via-teal-900 to-blue-900 bg-opacity-80 backdrop-blur-md shadow-md">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <span className="text-white font-bold text-xl bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-teal-400">
                                GreenCloud
                            </span>
                        </div>
                        <div className="hidden md:flex space-x-6">
                            <a
                                href="#features"
                                className="text-gray-300 hover:text-white transition duration-300"
                            >
                                Features
                            </a>
                            <a
                                href="#pricing"
                                className="text-gray-300 hover:text-white transition duration-300"
                            >
                                Pricing
                            </a>
                            <a
                                href="#contact"
                                className="text-gray-300 hover:text-white transition duration-300"
                            >
                                Contact
                            </a>

                            {authStatus === 'unauthenticated' && (
                                <a
                                    href="/login"
                                    className="text-gray-300 hover:text-white transition duration-300"
                                >
                                    Login
                                </a>
                            )}

                            {authStatus === 'authenticated' && (
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="text-gray-300 hover:text-white transition duration-300"
                                >
                                    Dashboard
                                </button>
                            )}
                        </div>
                    </div>
                </nav>
            </header>

            {/* Hero Section */}
            <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-24">
                <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-teal-500 animate-pulse">
                    GreenCloud Builder
                </h1>
                <p className="mt-4 text-lg md:text-2xl text-gray-200 max-w-2xl">
                    Build your own VPS from flexible resource bundles. Total
                    control. Maximum performance.
                </p>
                <a
                    href="#pricing"
                    className="mt-8 inline-block px-8 py-4 rounded-full bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold text-lg hover:from-green-400 hover:to-teal-400 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-green-500/50"
                >
                    Explore Resource Packs
                </a>
            </section>

            {/* Features Section */}
            <section id="features" className="py-16 px-4 md:px-8">
                <h2 className="text-4xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-teal-500">
                    Why Build with GreenCloud?
                </h2>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    <FeatureCard
                        title="Flexible Resources"
                        description="Mix and match CPU, RAM, and storage to create your perfect VPS environment."
                        delay={0}
                    />
                    <FeatureCard
                        title="Self-Service Control"
                        description="Deploy and manage your own VPS anytime, without waiting or support tickets."
                        delay={0.2}
                    />
                    <FeatureCard
                        title="Developer-Friendly"
                        description="API access and automation-ready infrastructure to streamline your workflow."
                        delay={0.4}
                    />
                </div>
            </section>

            {/* Pricing Section (dynamic from API) */}
            <section
                id="pricing"
                className="py-16 px-4 md:px-8 bg-gradient-to-br from-green-900/50 to-teal-900/50"
            >
                <h2 className="text-4xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-teal-500">
                    Choose Your Resource Pack
                </h2>

                <div className="mt-12 max-w-6xl mx-auto">
                    {pkgError && (
                        <div className="mb-6 rounded-lg border border-red-500/40 bg-red-900/30 p-4 text-red-200">
                            Failed to load packages: {pkgError}
                        </div>
                    )}

                    {buyErr && (
                        <div className="mb-6 rounded-lg border border-red-500/40 bg-red-900/30 p-4 text-red-200">
                            {buyErr}
                        </div>
                    )}

                    {buyMsg && (
                        <div className="mb-6 rounded-lg border border-emerald-500/40 bg-emerald-900/40 p-4 text-emerald-200">
                            {buyMsg}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {loadingPackages &&
                            [0, 1, 2].map((i) => (
                                <div
                                    key={i}
                                    className="p-6 rounded-xl bg-gradient-to-br from-green-800/30 to-teal-800/30 backdrop-blur-md animate-pulse"
                                >
                                    <div className="h-6 w-2/3 bg-white/20 rounded mb-4" />
                                    <div className="h-8 w-1/3 bg-white/20 rounded mb-6" />
                                    <div className="space-y-2">
                                        <div className="h-4 w-full bg-white/10 rounded" />
                                        <div className="h-4 w-11/12 bg-white/10 rounded" />
                                        <div className="h-4 w-10/12 bg-white/10 rounded" />
                                    </div>
                                </div>
                            ))}

                        {!loadingPackages &&
                            packages.map((pkg) => (
                                <div
                                    key={pkg.id}
                                    className="p-6 rounded-xl bg-gradient-to-br from-green-800/50 to-teal-800/50 backdrop-blur-md hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-green-500/50"
                                >
                                    <h3 className="text-2xl font-semibold text-green-300">
                                        {pkg.name}
                                    </h3>
                                    <p className="mt-2 text-3xl font-bold text-white">
                                        {formatPrice(pkg.price_per_month)}
                                    </p>
                                    {pkg.description && (
                                        <p className="mt-3 text-sm text-gray-300 line-clamp-3">
                                            {pkg.description}
                                        </p>
                                    )}
                                    <ul className="mt-4 text-gray-300 space-y-1">
                                        <li>{pkg.vcpus} vCPU</li>
                                        <li>{toGB(pkg.ram)} RAM</li>
                                        <li>{pkg.total_volume_gb} GB NVMe</li>
                                        <li>{pkg.instances} Instances</li>
                                    </ul>
                                    <button
                                        onClick={() => handleBuy(pkg)}
                                        disabled={buyingId === pkg.id}
                                        className={`mt-6 px-6 py-2 rounded-full text-white font-semibold transition-all duration-300 ${
                                            buyingId === pkg.id
                                                ? 'bg-gray-600 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-400 hover:to-teal-400'
                                        }`}
                                    >
                                        {buyingId === pkg.id
                                            ? 'Processing…'
                                            : `Buy ${pkg.name.split(' ')[0]}`}
                                    </button>
                                </div>
                            ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-16 text-center text-gray-300">
                <h2 className="text-3xl font-bold mb-4 text-white">
                    Get in Touch
                </h2>
                <p>
                    Email us at{' '}
                    <a
                        href="mailto:support@greencloud.com"
                        className="underline text-teal-300"
                    >
                        support@greencloud.com
                    </a>
                </p>
            </section>
        </div>
    )
}

export default Home
